'use client';

import React, { FC, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import classNames from 'classnames';

import { UniversalCta } from '@/components';
import { getMediaComponent } from '@/components/common/LandingPageHero/utils';
import { ExtendedTypeHomepageHeroFields } from '@/components/common/HomepageHero/HomepageHero';
import { useIsDesktop } from '@/hooks/useIsDesktop';

import type { TypeVideoFields } from '@/core/generated-types';
import type { TypeMuxVideoFields } from '@/core/generated-types';

const useVideoReady = () => {
  const [isMobileVideoReady, setIsMobileVideoReady] = useState(false);
  const [isDesktopVideoReady, setIsDesktopVideoReady] = useState(false);

  return {
    isMobileVideoReady,
    setIsMobileVideoReady,
    isDesktopVideoReady,
    setIsDesktopVideoReady,
  };
};

export const Parallax: FC<ExtendedTypeHomepageHeroFields> = ({
  entryName,
  desktopMedia,
  mobileMedia,
  subtext = '',
  headerText = '',
  cta,
  secondaryCTA,
  desktopCtaAlignment,
  mobileMediaIsVideo,
}) => {
  const isDesktop = useIsDesktop();
  const [hydrated, setHydrated] = useState(false);

  const {
    isMobileVideoReady,
    setIsMobileVideoReady,
    isDesktopVideoReady,
    setIsDesktopVideoReady,
  } = useVideoReady();

  const mediaWithPoster = desktopMedia.fields as
    | TypeVideoFields
    | TypeMuxVideoFields;

  const mobileComponent = useMemo(
    () =>
      getMediaComponent({
        media: mobileMedia,
        classes: `w-full aspect-video`,
        autoplay: true,
        videoStatus: {
          isReady: isMobileVideoReady,
          setIsReady: setIsMobileVideoReady,
        },
      }),
    [mobileMedia, isMobileVideoReady, setIsMobileVideoReady],
  );
  const desktopComponent = useMemo(
    () =>
      getMediaComponent({
        media: desktopMedia,
        classes: `w-full aspect-video`,
        autoplay: true,
        videoStatus: {
          isReady: isDesktopVideoReady,
          setIsReady: setIsDesktopVideoReady,
        },
      }),
    [desktopMedia, isDesktopVideoReady, setIsDesktopVideoReady],
  );
  const mediaComponent = isDesktop ? desktopComponent : mobileComponent;

  useEffect(() => {
    setHydrated(true);
  }, []);

  return (
    <section
      data-testid={`homepage-hero-${entryName}`}
      className="display-unset"
    >
      <div className="relative">
        {/* Video poster with matching aspect ratio to prevent layout shift during hydration */}
        {!hydrated ? (
          <div className="aspect-video bg-slate-300">
            {mediaWithPoster.poster && (
              <Image
                className="aspect-video"
                src={`https:${mediaWithPoster.poster.fields.file.url}`}
                width={mediaWithPoster.poster.fields.file.details.image?.width}
                height={
                  mediaWithPoster.poster.fields.file.details.image?.height
                }
                alt="poster image"
              />
            )}
          </div>
        ) : (
          <div
            className={classNames(
              !mobileMediaIsVideo ? 'h-[280px] lg:h-[970px]' : '',
            )}
          >
            {mediaComponent}
          </div>
        )}
        <div className="relative lg:absolute lg:top-1/2 lg:left-0 lg:transform lg:-translate-y-1/2 w-full">
          <div className="mx-auto max-w-[1400px] pt-3 pb-9 text-grey-dark bg-off-white lg:bg-transparent lg:text-white pl-0 lg:pl-6">
            <div className="lg:max-w-[700px] text-center lg:text-left">
              <h1
                className="text-[36px] font-bold leading-10 px-5 lg:text-[65px] lg:px-0 lg:leading-[70px]"
                dangerouslySetInnerHTML={{ __html: headerText }}
              />
              {subtext && (
                <h4
                  className="text-d-base mt-2 font-bold lg:text-3xl px-10 lg:px-0 lg:max-w-[700px] lg:mt-7 lg:mb-5"
                  dangerouslySetInnerHTML={{ __html: subtext }}
                />
              )}
              <div
                className={classNames(
                  'flex flex-col lg:inline-flex',
                  desktopCtaAlignment === 'row' ? 'gap-4 lg:flex-row' : 'gap-3',
                  secondaryCTA && desktopCtaAlignment === 'row'
                    ? 'lg:w-[100%]'
                    : '',
                )}
              >
                {cta && (
                  <div className="px-6 lg:px-0">
                    <UniversalCta
                      link={cta}
                      defaultButtonStyle="Primary"
                      className={classNames('mt-4 h-14 lg:h-[62px]', {
                        'lg:grow lg:basis-0': secondaryCTA,
                      })}
                    />
                  </div>
                )}
                {secondaryCTA && (
                  <UniversalCta
                    link={secondaryCTA}
                    defaultButtonStyle="Secondary"
                    className="lg:grow lg:basis-0"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
